import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import {
  CustomStatus,
  StatusType,
  Team,
  Workflow,
  WorkflowAction,
  WorkflowStatus,
  WorkflowType,
  WorkflowStep,
  WorkflowStepTeamAssignment
} from '../../../../core/models/models';
import { CustomStatusService } from '../../../../core/services/custom-status.service';
import { TeamService } from '../../../../core/services/team.service';
import { WorkflowService } from '../../../../core/services/workflow.service';
import { ConfirmationDialogComponent } from '../../../../shared/components/dialogs/confirmation-dialog.component';

@Component({
  selector: 'app-workflow-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    DragDropModule,
    ConfirmationDialogComponent
  ],
  templateUrl: './workflow-form.component.html',
  styleUrl: './workflow-form.component.scss'
})
export class WorkflowFormComponent implements OnInit {
  statuses: CustomStatus[] = [];
  teams: Team[] = [];
  errorMessage = '';
  validationErrors: string[] = [];
  stepErrors: Record<number, string> = {}; // step index -> error message
  isLoading = true;
  isSaving = false;
  successMessage = '';
  isEditMode = false;
  workflowId: number | null = null;
  isDeleteConfirmOpen = false;

  statusesEnum = Object.values(WorkflowStatus);
  workflowTypes = Object.values(WorkflowType);
  permissions = Object.values(WorkflowAction);

  form: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly workflowService: WorkflowService,
    private readonly customStatusService: CustomStatusService,
    private readonly teamService: TeamService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      status: [WorkflowStatus.DRAFT, Validators.required],
      type: [WorkflowType.PROJECT, Validators.required],
      steps: this.fb.array([])
    });
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.isEditMode = true;
      this.workflowId = Number(idParam);
    } else {
      const initialType = this.route.snapshot.queryParamMap.get('type') as WorkflowType | null;
      if (initialType && Object.values(WorkflowType).includes(initialType)) {
        this.form.patchValue({ type: initialType });
      }
    }

    this.loadFormData();
  }

  get steps(): FormArray {
    return this.form.get('steps') as FormArray;
  }

  getAssignments(stepIndex: number): FormArray {
    return this.steps.at(stepIndex).get('teamAssignments') as FormArray;
  }

  addStep(): void {
    this.steps.push(this.createStepGroup());
    this.reorderSteps();
  }

  removeStep(index: number): void {
    if (this.steps.length <= 1) {
      this.errorMessage = 'Un workflow doit contenir au moins une étape.';
      this.validationErrors = [this.errorMessage];
      this.cdr.detectChanges();
      return;
    }

    this.steps.removeAt(index);
    this.reorderSteps();
  }

  addAssignment(stepIndex: number): void {
    this.getAssignments(stepIndex).push(this.createAssignmentGroup());
  }

  removeAssignment(stepIndex: number, assignmentIndex: number): void {
    this.getAssignments(stepIndex).removeAt(assignmentIndex);
  }

  onStepDropped(event: CdkDragDrop<WorkflowStep[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.steps.controls, event.previousIndex, event.currentIndex);
    this.reorderSteps();
  }

  togglePermission(
    stepIndex: number,
    assignmentIndex: number,
    permission: WorkflowAction,
    checked: boolean
  ): void {
    const control = this.getAssignments(stepIndex).at(assignmentIndex).get('permissions');
    const current: WorkflowAction[] = [...(control?.value ?? [])];

    if (checked && !current.includes(permission)) {
      current.push(permission);
    }

    if (!checked) {
      const idx = current.indexOf(permission);
      if (idx >= 0) {
        current.splice(idx, 1);
      }
    }

    control?.setValue(current);
  }

  hasPermission(stepIndex: number, assignmentIndex: number, permission: WorkflowAction): boolean {
    const current: WorkflowAction[] =
      this.getAssignments(stepIndex).at(assignmentIndex).get('permissions')?.value ?? [];
    return current.includes(permission);
  }

  onSave(): void {
    if (this.form.invalid || this.steps.length === 0) {
      this.form.markAllAsTouched();
      this.errorMessage = 'Merci de compléter le formulaire. Certains champs obligatoires sont manquants ou invalides.';
      this.validationErrors = [this.errorMessage];
      this.cdr.detectChanges();
      return;
    }

    this.errorMessage = '';
    this.validationErrors = [];
    this.stepErrors = {};

    const frontValidation = this.validateBeforeSave();
    if (frontValidation.globalErrors.length > 0 || Object.keys(frontValidation.stepErrors).length > 0) {
      this.validationErrors = frontValidation.globalErrors;
      this.stepErrors = frontValidation.stepErrors;
      if (this.validationErrors.length === 0 && Object.keys(this.stepErrors).length > 0) {
        this.errorMessage = 'Des erreurs ont été détectées dans les étapes du workflow.';
      } else if (this.validationErrors.length > 0) {
        this.errorMessage = this.validationErrors[0];
      }
      this.cdr.detectChanges();
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.cdr.detectChanges();

    const payload: Workflow = {
      id: this.workflowId ?? undefined,
      name: this.form.value.name as string,
      description: (this.form.value.description as string) ?? '',
      status: this.form.value.status as WorkflowStatus,
      type: this.form.value.type as WorkflowType,
      steps: (this.form.value.steps as WorkflowStep[]) ?? []
    };

    const request$ = this.isEditMode && this.workflowId
      ? this.workflowService.updateWorkflow(this.workflowId, payload)
      : this.workflowService.createWorkflow(payload);

    request$.subscribe({
      next: () => {
        this.isSaving = false;
        this.successMessage = this.isEditMode
          ? 'Workflow mis a jour avec succes.'
          : 'Workflow cree avec succes.';
        this.cdr.detectChanges();
        setTimeout(() => this.router.navigate(['/settings/workflows']), 900);
      },
      error: (err) => {
        this.isSaving = false;
        let detailedError = '';

        if (err?.error) {
          if (typeof err.error === 'string') {
            detailedError = err.error;
          } else if (err.error.message) {
            // Priorité absolue au champ 'message' que nous avons normalisé en back
            detailedError = err.error.message;
          } else if (err.error.error) {
            detailedError = err.error.error;
          }
        }

        this.errorMessage = detailedError || err?.message || 'Erreur lors de la sauvegarde du workflow';
        this.validationErrors = [this.errorMessage];
        this.cdr.detectChanges();
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/settings/workflows']);
  }

  onRequestDelete(): void {
    if (!this.isEditMode || !this.workflowId) {
      return;
    }
    this.isDeleteConfirmOpen = true;
  }

  confirmDelete(): void {
    if (!this.workflowId) {
      return;
    }
    this.workflowService.deleteWorkflow(this.workflowId).subscribe({
      next: () => {
        this.successMessage = 'Suppression/archivage du workflow effectuée avec succès.';
        this.isDeleteConfirmOpen = false;
        this.cdr.detectChanges();
        setTimeout(() => this.router.navigate(['/settings/workflows']), 700);
      },
      error: (err) => {
        let detailedError = '';
        if (err?.error) {
          if (typeof err.error === 'string') {
            detailedError = err.error;
          } else if (err.error.message) {
            detailedError = err.error.message;
          } else if (err.error.error) {
            detailedError = err.error.error;
          }
        }
        this.errorMessage = detailedError || err?.message || 'Erreur lors de la suppression/archivage du workflow';
        this.isDeleteConfirmOpen = false;
        this.cdr.detectChanges();
      }
    });
  }

  cancelDelete(): void {
    this.isDeleteConfirmOpen = false;
  }

  getDeleteDialogTitle(): string {
    return 'Supprimer / Archiver le workflow';
  }

  getDeleteDialogMessage(): string {
    return 'Le workflow sera supprimé si possible, sinon archivé selon son état et ses liaisons. Continuer ?';
  }

  getDeleteDialogLabel(): string {
    return 'Confirmer';
  }

  isSequenceError(): boolean {
    if (typeof this.errorMessage !== 'string') return false;
    return this.errorMessage.toLowerCase().includes('incoherence de sequence') ||
      this.validationErrors.some(e => e.toLowerCase().includes('incoherence de sequence'));
  }

  private loadFormData(): void {
    this.isLoading = true;
    this.cdr.detectChanges();

    const workflow$ = this.isEditMode && this.workflowId
      ? this.workflowService.getWorkflowById(this.workflowId)
      : of(null);

    forkJoin({
      statusesPage: this.customStatusService.getPage('', 0, 1000, 'name,asc', true, 'ALL', this.selectedStatusType()),
      teamsPage: this.teamService.getTeams('', 0, 1000, 'name,asc', true),
      workflow: workflow$
    }).subscribe({
      next: ({ statusesPage, teamsPage, workflow }) => {
        this.statuses = statusesPage.content;
        this.teams = teamsPage.content;

        if (workflow) {
          this.form.patchValue({
            name: workflow.name ?? '',
            description: workflow.description ?? '',
            status: workflow.status ?? WorkflowStatus.DRAFT,
            type: workflow.type ?? WorkflowType.PROJECT
          });
          this.loadStatusesForCurrentType();

          this.setSteps(workflow.steps ?? []);
        } else if (this.steps.length === 0) {
          this.addStep();
        }

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.errorMessage = 'Erreur lors du chargement du formulaire workflow';
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private setSteps(workflowSteps: WorkflowStep[]): void {
    this.steps.clear();

    const sorted = [...workflowSteps].sort((a, b) => a.stepOrder - b.stepOrder);
    for (const step of sorted) {
      this.steps.push(this.createStepGroup(step));
    }

    if (this.steps.length === 0) {
      this.addStep();
    }

    this.reorderSteps();
  }

  private createStepGroup(step?: WorkflowStep): FormGroup {
    return this.fb.group({
      id: [step?.id ?? null],
      name: [step?.name ?? '', Validators.required],
      description: [step?.description ?? ''],
      stepOrder: [step?.stepOrder ?? this.steps.length + 1, Validators.required],
      initialStatusId: [step?.initialStatusId ?? null, Validators.required],
      finalStatusId: [step?.finalStatusId ?? null, Validators.required],
      teamAssignments: this.fb.array(
        (step?.teamAssignments ?? []).map((assignment) => this.createAssignmentGroup(assignment))
      )
    });
  }

  private createAssignmentGroup(assignment?: WorkflowStepTeamAssignment): FormGroup {
    return this.fb.group({
      id: [assignment?.id ?? null],
      teamId: [assignment?.teamId ?? null, Validators.required],
      permissions: [assignment?.permissions ?? [WorkflowAction.VALIDATE, WorkflowAction.REJECT]]
    });
  }

  private reorderSteps(): void {
    this.steps.controls.forEach((ctrl, idx) => {
      ctrl.get('stepOrder')?.setValue(idx + 1);
    });
  }

  private validateBeforeSave(): { globalErrors: string[], stepErrors: Record<number, string> } {
    const globalErrors: string[] = [];
    const stepErrors: Record<number, string> = {};

    const orderedSteps = this.steps.controls
      .map((ctrl, arrayIdx) => ({
        arrayIdx,
        index: Number(ctrl.get('stepOrder')?.value),
        name: String(ctrl.get('name')?.value ?? ''),
        initialStatusId: Number(ctrl.get('initialStatusId')?.value),
        finalStatusId: Number(ctrl.get('finalStatusId')?.value),
        assignments: this.readAssignments(ctrl.get('teamAssignments') as FormArray)
      }))
      .sort((a, b) => a.index - b.index);

    for (let i = 0; i < orderedSteps.length; i++) {
      const step = orderedSteps[i];

      if (!step.assignments.length) {
        stepErrors[step.arrayIdx] = `L'étape ${i + 1} (${step.name || 'sans nom'}) doit avoir au moins une équipe associée.`;
      } else {
        const permissions = new Set(step.assignments.flatMap((a) => a.permissions));
        if (!permissions.has(WorkflowAction.VALIDATE) || !permissions.has(WorkflowAction.REJECT)) {
          stepErrors[step.arrayIdx] = `L'étape ${i + 1} doit couvrir les actions Valider et Rejeter.`;
        }
      }

      if (i < orderedSteps.length - 1) {
        const next = orderedSteps[i + 1];
        if (step.finalStatusId !== next.initialStatusId) {
          const msg = `Incohérence de séquence: le statut final de l'étape ${i + 1} doit être identique au statut initial de l'étape ${i + 2}.`;
          stepErrors[step.arrayIdx] = msg;
          stepErrors[next.arrayIdx] = msg;
        }
      }
    }

    return { globalErrors, stepErrors };
  }

  onWorkflowTypeChanged(): void {
    this.loadStatusesForCurrentType();
  }

  private loadStatusesForCurrentType(): void {
    this.customStatusService.getPage('', 0, 1000, 'name,asc', true, 'ALL', this.selectedStatusType()).subscribe({
      next: page => {
        this.statuses = page.content;
        this.cdr.detectChanges();
      },
      error: () => {
        this.errorMessage = 'Erreur lors du chargement des statuts du type sélectionné';
        this.cdr.detectChanges();
      }
    });
  }

  private selectedStatusType(): StatusType {
    const workflowType = (this.form.get('type')?.value as WorkflowType | null) ?? WorkflowType.PROJECT;
    return workflowType === WorkflowType.FAT ? StatusType.FAT : StatusType.PROJECT;
  }

  private readAssignments(assignmentsArray: FormArray): Array<{ teamId: number; permissions: WorkflowAction[] }> {
    return assignmentsArray.controls
      .map((assignmentCtrl) => ({
        teamId: Number(assignmentCtrl.get('teamId')?.value),
        permissions: (assignmentCtrl.get('permissions')?.value ?? []) as WorkflowAction[]
      }))
      .filter((assignment) => !!assignment.teamId);
  }
}
